import {
  InputRule,
  markInputRule,
  markPasteRule,
  PasteRule,
} from '@tiptap/core';
import Link, { type LinkOptions } from '@tiptap/extension-link';

const inputRegex = /(?:^|\s)\[([^\]]*)?\]\((\S+)(?: "(.+)")?\)$/i;
const pasteRegex = /(?:^|\s)\[([^\]]*)?\]\((\S+)(?: "(.+)")?\)/gi;

function linkInputRule(config: Parameters<typeof markInputRule>[0]) {
  const defaultMarkInputRule = markInputRule(config);

  return new InputRule({
    find: config.find,
    handler(props) {
      const { tr } = props.state;
      defaultMarkInputRule.handler(props);
      tr.setMeta('autolink', false);
    },
  });
}

function linkPasteRule(config: Parameters<typeof markPasteRule>[0]) {
  const defaultMarkPasteRule = markPasteRule(config);

  return new PasteRule({
    find: config.find,
    handler(props) {
      const { tr } = props.state;

      defaultMarkPasteRule.handler(props);
      tr.setMeta('autolink', false);
    },
  });
}

/**
 * Custom extension that extends the built-in `Link` extension to add additional input/paste rules
 * for converting the Markdown link syntax (i.e. `[Nasa](https://nasa.gov)`) into links, and also
 * adds support for the `title` attribute. (i.e. `[Nasa](https://nasa.gov "NASA")`)
 */
export const MarkdownLink = Link.extend<LinkOptions>({
  inclusive: false,
  addOptions() {
    return {
      ...this.parent?.()!,
      openOnClick: 'whenNotEditable',
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      title: {
        default: null,
      },
    };
  },
  addInputRules() {
    return [
      linkInputRule({
        find: inputRegex,
        type: this.type,

        // We need to use `pop()` to remove the last capture groups from the match to
        // satisfy Tiptap's `markPasteRule` expectation of having the content as the last
        // capture group in the match (this makes the attribute order important)
        getAttributes(match) {
          return {
            title: match.pop()?.trim(),
            href: match.pop()?.trim(),
          };
        },
      }),
    ];
  },
  addPasteRules() {
    return [
      linkPasteRule({
        find: pasteRegex,
        type: this.type,

        // We need to use `pop()` to remove the last capture groups from the match to
        // satisfy Tiptap's `markInputRule` expectation of having the content as the last
        // capture group in the match (this makes the attribute order important)
        getAttributes(match) {
          return {
            title: match.pop()?.trim(),
            href: match.pop()?.trim(),
          };
        },
      }),
    ];
  },
});
